import { AiDocumentSelectionModal } from "@/features/recruitee/components/aiDocumentSelectionModal";
import {
	Autocomplete,
	Badge,
	Button,
	Card,
	Flex,
	Group,
	Loader,
	Stack,
	Table,
	Text,
	useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconRefresh, IconSearch } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import type {
	GetApiV1SelectionsJobInformation200JobInformationItem,
	PostApiV1SelectionsSelectionIdApplicantResume200,
} from "../../../../api/recrutaAI/generated/aISaaSAPI.schemas";

type Props = {
	recruitPositions: GetApiV1SelectionsJobInformation200JobInformationItem[];
	handleReload: () => void;
	recruiteeDetailDrawerOpen: () => void;
	setResume: (resume: PostApiV1SelectionsSelectionIdApplicantResume200) => void;
	disabledAiDocumentSelection: boolean;
};

export function RecruitPositionList({
	recruitPositions,
	handleReload,
	recruiteeDetailDrawerOpen,
	setResume,
	disabledAiDocumentSelection,
}: Props) {
	const theme = useMantineTheme();
	const navigate = useNavigate();

	const [selectionId, setSelectionId] = useState<string>("");

	const [opened, { open, close }] = useDisclosure(false);

	const [search, setSearch] = useState<string>("");

	const filteredRecruitPositions = recruitPositions.filter((recruitPosition) =>
		recruitPosition.name.toLowerCase().includes(search.toLowerCase()),
	);

	return (
		<>
			<Card withBorder p={0} radius="md" h="100%">
				<Group justify="space-between" p={24}>
					<Autocomplete
						placeholder="選考基準名で検索"
						leftSection={<IconSearch size={16} stroke={1.5} />}
						data={[
							...new Set(
								recruitPositions.map((recruitPosition) => recruitPosition.name),
							),
						]}
						visibleFrom="xs"
						onChange={(value) => setSearch(value)}
					/>
				</Group>
				<Table.ScrollContainer
					h="calc(100vh - 480px)"
					minWidth={500}
					type="native"
				>
					<Table
						highlightOnHover
						striped
						stripedColor={theme.colors.gray[0]}
						stickyHeader
						horizontalSpacing="xl"
						verticalSpacing="sm"
					>
						<Table.Thead>
							<Table.Tr>
								<Table.Th pl="md" colSpan={4}>
									選考基準名
								</Table.Th>
							</Table.Tr>
						</Table.Thead>
						<Table.Tbody>
							{filteredRecruitPositions.map((recruitPosition) => {
								if (
									recruitPosition.status !== "completed" &&
									recruitPosition.status !== "failed"
								) {
									return (
										<Table.Tr key={recruitPosition.jobInformationId}>
											<Table.Td colSpan={4} p="md">
												<Stack gap="xs">
													<Flex gap="xs" align="center">
														<Text>{recruitPosition.name}</Text>
														<Badge
															variant="light"
															size="lg"
															leftSection={<Loader type="bars" size={16} />}
														>
															選考基準を生成中...
														</Badge>
														<Button
															variant="outline"
															size="xs"
															leftSection={<IconRefresh size={16} />}
															onClick={handleReload}
														>
															再読み込み
														</Button>
													</Flex>
													<Text size="xs">
														生成が完了するまでに数分かかる場合があります。定期的に「再読み込み」ボタンを押して確認をしてください。
													</Text>
												</Stack>
											</Table.Td>
										</Table.Tr>
									);
								}

								return (
									<>
										<Table.Tr key={recruitPosition.jobInformationId}>
											<Table.Td pl="md" colSpan={3}>
												<Text c="primary">
													{/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
													<span
														style={{ cursor: "pointer" }}
														onClick={() =>
															navigate({
																to: "/recruit-positions/$recruitPositionId",
																params: {
																	recruitPositionId: String(
																		recruitPosition.jobInformationId,
																	),
																},
															})
														}
													>
														{recruitPosition.name}
													</span>
													{recruitPosition.sample && (
														<Badge variant="light" ml={12}>
															サンプル
														</Badge>
													)}
													{recruitPosition.status === "failed" && (
														<Badge variant="light" color="red" ml={12}>
															生成エラー
														</Badge>
													)}
												</Text>
											</Table.Td>
											<Table.Td colSpan={1}>
												<Flex justify="flex-end">
													<Button
														variant="outline"
														size="xs"
														onClick={() => {
															setSelectionId(recruitPosition.jobInformationId);
															open();
														}}
														disabled={
															disabledAiDocumentSelection ||
															recruitPosition.status === "failed"
														}
													>
														AI書類選考
													</Button>
												</Flex>
											</Table.Td>
										</Table.Tr>
									</>
								);
							})}
						</Table.Tbody>
					</Table>
				</Table.ScrollContainer>
			</Card>
			<AiDocumentSelectionModal
				// TODO: API結合
				selectionId={selectionId}
				opened={opened}
				close={close}
				recruiteeDetailDrawerOpen={recruiteeDetailDrawerOpen}
				setResume={setResume}
			/>
		</>
	);
}
